// Benutzer-Service (CMS) — spricht die Admin-Endpunkte unter /api/users.
// Muster wie mindBookService/topicCategoryService: eigene axios-Instanz + Bearer aus der
// Supabase-Session.
//
// ⚠️ Alle drei hier benutzten Endpunkte hängen serverseitig an `requireAdmin` (userRoutes.ts),
// und der prüft strikt auf `role === 'ADMIN'` — ein Moderator bekommt 403, nicht eine leere Liste.
// Deshalb steht der Menüpunkt nur für Administratoren.
//
// ⚠️ `DELETE /api/users/:id` gibt es, wird hier aber bewusst NICHT angebunden. Der reversible Weg
// ist das Deaktivieren, und das CMS folgt dieser Doktrin bereits bei den Themen-Kategorien.
// (Nebenbefund am Backend: der Endpunkt löscht ohnehin nicht, er setzt `isActive = false` —
// derselbe Effekt unter einem Namen, der etwas anderes verspricht.)
import axios, { AxiosInstance } from 'axios';
import { ApiPagination, ApiResponse, PaginatedResponse, UserFilters, UserRole, normalizePagination } from '../types';
import { getApiBaseUrl } from '../utils/apiConfig';
import { getAccessToken } from '../lib/authToken';

/**
 * Eine Zeile der Benutzer-Liste — spiegelt EXAKT `BaseUserQueryService.getUserSelect()` des
 * Backends, mehr Felder liefert der Listen-Endpunkt nicht.
 *
 * Bewusst nicht `User` aus `types/index.ts`: das ist die Antwortform von `GET /api/users/me` und
 * kennt ausgerechnet `isActive` nicht — also genau das Feld, das diese Seite umschaltet.
 */
export interface UserListItem {
  id: string;
  email: string;
  username: string;
  firstName: string;
  lastName: string;
  role: UserRole;
  isActive: boolean;
  isEmailVerified: boolean;
  createdAt: string;
  /** Noch nie angemeldet → `null`. */
  lastLoginAt?: string | null;
}

/**
 * Was `GET /api/users/:id/public` zurückgibt — spiegelt EXAKT das `select` in
 * `UserService.getPublicProfile` des Backends, ohne die hier ungenutzten `avatar`, `profile` und
 * `createdAt`.
 */
export interface OeffentlichesProfil {
  id: string;
  username: string;
  firstName: string;
  lastName: string;
}

class UserService {
  private api: AxiosInstance;

  constructor() {
    this.api = axios.create({
      baseURL: getApiBaseUrl(),
      timeout: 15000,
      headers: { 'Content-Type': 'application/json' },
    });
    this.api.interceptors.request.use(async (config) => {
      const token = await getAccessToken();
      if (token) config.headers.Authorization = `Bearer ${token}`;
      return config;
    });
  }

  /**
   * ⚠️ Die Liste steckt eine Ebene tiefer als bei den Medien-Endpunkten: `data.users`, nicht
   * `data.content` und nicht `data` selbst. Und die Seitenzahl heißt hier `pages` statt
   * `totalPages` — `normalizePagination` gleicht genau das an.
   *
   * `signal` ist der Abbruch-Weg für die tippende Suche: axios kennt kein `AbortSignal` von sich
   * aus, es muss als Feld der Request-Config durchgereicht werden (`{ signal }`) — sonst läuft der
   * Request trotz `controller.abort()` weiter und seine veraltete Antwort landet in der Tabelle.
   * Beim Abbruch wirft axios einen `CanceledError`; die Oberfläche filtert ihn (`istAbbruch`),
   * statt ihn als Fehlermeldung anzuzeigen.
   */
  async getUsers(filters: UserFilters, signal?: AbortSignal): Promise<PaginatedResponse<UserListItem>> {
    const params = new URLSearchParams();
    // Leere Werte NICHT mitschicken: ein `role=` ohne Wert würde serverseitig als Filter gewertet
    // und die Liste leeren.
    Object.entries(filters).forEach(([key, value]) => {
      if (value !== undefined && value !== null && value !== '') params.append(key, String(value));
    });
    const response = await this.api.get<{
      success: boolean;
      data: { users: UserListItem[]; pagination: ApiPagination };
    }>(`/api/users?${params.toString()}`, { signal });

    if (response.data.success && response.data.data) {
      return {
        data: response.data.data.users || [],
        pagination: normalizePagination(response.data.data.pagination),
      };
    }
    return { data: [], pagination: normalizePagination(null) };
  }

  /**
   * Rollenwechsel. Gibt bewusst NICHTS zurück: die Antwort trägt nur einen Ausschnitt des
   * Datensatzes (id, email, username, role, updatedAt) — ein `UserListItem` daraus zu behaupten
   * wäre gelogen. Die Seite lädt nach dem Schreiben neu.
   */
  async updateRole(id: string, role: UserRole): Promise<void> {
    const response = await this.api.put<{ success: boolean; message?: string }>(
      `/api/users/${id}/role`,
      { role }
    );
    if (!response.data.success) throw new Error(response.data.message || 'Rollenwechsel fehlgeschlagen');
  }

  /** Aktivieren/Deaktivieren. Antwortform wie bei `updateRole` — siehe dort. */
  async updateStatus(id: string, isActive: boolean): Promise<void> {
    const response = await this.api.put<{ success: boolean; message?: string }>(
      `/api/users/${id}/status`,
      { isActive }
    );
    if (!response.data.success) throw new Error(response.data.message || 'Statuswechsel fehlgeschlagen');
  }

  /**
   * Ein einzelnes Konto zum Anzeigen eines NAMENS auflösen.
   *
   * ⚠️ Bewusst `GET /api/users/:id/public` und ausdrücklich NICHT `GET /api/users/:id`. Der
   * naheliegende Weg wäre der zweite — er liefert mehr Felder. Er hängt aber an `requireAdmin`
   * (`userRoutes.ts`), und der prüft strikt auf `role === 'ADMIN'`. Der einzige Verbraucher dieser
   * Methode ist die Fassungsliste der Inhalte-Seiten, und deren Endpunkt steht ADMIN, MODERATOR
   * UND CONTENT_CREATOR offen (`contentRoutes.ts:387`). Für die beiden letzten Rollen wäre der
   * Admin-Weg ein 403 auf jeder Zeile: die Liste sähe für sie aus, als kenne das System keinen
   * einzigen Namen. `/public` hängt an gar keiner Rechteprüfung und liefert genau das, was
   * angezeigt wird.
   *
   * ⚠️ Was der Endpunkt NICHT liefert: `email` und `role`. Und er filtert auf `isActive: true`
   * (`UserService.getPublicProfile`) — ein deaktiviertes Konto ergibt 404, obwohl es die Zeile
   * geschrieben hat. Das ist kein Defekt dieser Methode, sondern eine Grenze, die der Aufrufer
   * kennen und ehrlich anzeigen muss („Unbekannt" statt einer Kennung).
   *
   * Wirft bei 404 und bei jedem anderen Fehlschlag — axios lehnt beides ab. Die Entscheidung, was
   * ein nicht auflösbarer Name bedeutet, gehört an die Anzeige, nicht hierher; der Aufrufer fängt
   * das über `Promise.allSettled` ab.
   */
  async getPublicProfile(id: string): Promise<OeffentlichesProfil | null> {
    const response = await this.api.get<ApiResponse<OeffentlichesProfil>>(`/api/users/${id}/public`);
    return response.data.success && response.data.data ? response.data.data : null;
  }
}

export const userService = new UserService();
